import {
  THEME_DARK,
  THEME_LIGHT,
  HARD_MODE_HARD,
  HARD_MODE_NORMAL,
  EXPERT_MODE_EXPERT,
  EXPERT_MODE_NORMAL,
  REVEAL_SPEED_FAST,
  CURSOR_TYPE_NORMAL,
  HINTS_DISABLE,
  HINTS_ENABLE,
  GameMode,
} from '../constants/strings'
import { v4 as uuidv4 } from 'uuid'
import { AchievementKey } from '../lib/achievements'

const DEVICE_KEY = 'deviceId'
const GAME_STATE_KEY = 'gameState'
const UNLIMITED_STATE_KEY = 'unlimitedState'
const HIGH_CONTRAST_KEY = 'highContrast'
const REVEAL_SPEED_KEY = 'revealSpeed'
const HARD_MODE_KEY = 'gameMode' // don't modify even though this is confusing
const FEATURES_KEY = 'features'
const CURSOR_TYPE_KEY = 'cursor'
const ENABLE_HINTS_KEY = 'hints'

const EXPERT_MODE_KEY = 'expertMode'
const THEME_KEY = 'theme'
const IS_SIGNED_IN_KEY = 'isSignedIn'
const HAS_SEEN_HINT_KEY = 'hasSeenHint'

export const getStoredDeviceId = () => {
  const id = localStorage.getItem(DEVICE_KEY) ?? ''
  if (id) {
    return id
  }
  const newId = uuidv4()
  // generate an id to identify this device.
  // Clearing the browser will wipe this,
  // but it will also wipe local stats
  localStorage.setItem(DEVICE_KEY, newId)
  return newId
}

export type StoredGameState = {
  gameMode: string
  guesses: string[]
  solution: string
  hard?: boolean // only set when there are guesses
  expert?: boolean // only set when there are guesses
  gaveUp?: boolean
}

export const setStoredGameStates = (gameState: StoredGameState[]) => {
  localStorage.setItem(GAME_STATE_KEY, JSON.stringify(gameState))
}

export const getStoredGameStates = () => {
  const state = localStorage.getItem(GAME_STATE_KEY)
  if (!state) {
    return []
  }
  const parsedStates = JSON.parse(state) as {}
  return Array.isArray(parsedStates) ? (parsedStates as StoredGameState[]) : []
}

export const getStoredGameState = (gameMode: string) => {
  const states = getStoredGameStates()
  return states.find((s: StoredGameState) => s.gameMode === gameMode)
}

export const setStoredGameState = (gs: StoredGameState) => {
  const states = getStoredGameStates()
  const index = states.findIndex(
    (s: StoredGameState) => gs.gameMode === s.gameMode
  )
  if (index === -1) {
    states.push(gs)
  } else {
    states[index] = gs
  }
  setStoredGameStates(states)
}

export type UnlimitedState = {
  index: number
  seed: string
}

export const setStoredUnlimitedState = (unlimitedState: UnlimitedState) => {
  localStorage.setItem(UNLIMITED_STATE_KEY, JSON.stringify(unlimitedState))
}

export const getStoredUnlimitedState = () => {
  const state = localStorage.getItem(UNLIMITED_STATE_KEY)
  return state ? (JSON.parse(state) as UnlimitedState) : null
}

const GAME_STAT_KEY = 'gameStats'
const USER_GAME_STAT_KEY = 'userGameStats'

export type GAME_STAT_TYPE = 'user' | 'local'
export const GAME_STAT_TYPE_LOCAL: GAME_STAT_TYPE = 'local'
export const GAME_STAT_TYPE_USER: GAME_STAT_TYPE = 'user'
export type Solve = {
  mode: GameMode
  solution: string
  guesses: number
  hard: boolean
  expert: boolean
}

export type GameStats = {
  winDistribution: number[]
  gamesFailed: number
  currentStreak: number
  bestStreak: number
  totalGames: number
  successRate: number
  lastSyncedAt?: string // timestamp
  syncedDeviceIds?: string[]
  type?: GAME_STAT_TYPE
  override?: boolean
  solves?: Solve[]
  achievements?: AchievementKey[]
}

export const setStoredStats = (gameStats: GameStats, uid: string | null) => {
  gameStats.type = uid ? GAME_STAT_TYPE_USER : GAME_STAT_TYPE_LOCAL
  if (!uid) {
    localStorage.setItem(GAME_STAT_KEY, JSON.stringify(gameStats))
    return
  }
  const userStats = getUserStats()
  userStats[uid] = gameStats
  localStorage.setItem(USER_GAME_STAT_KEY, JSON.stringify(userStats))
}

export const getStoredStats = (uid: string | null) => {
  if (!uid) {
    const stats = localStorage.getItem(GAME_STAT_KEY)
    return stats ? (JSON.parse(stats) as GameStats) : null
  }
  return getUserStats()[uid] ?? null
}

export const getUserStats = () => {
  const stats = localStorage.getItem(USER_GAME_STAT_KEY)
  return stats ? JSON.parse(stats) : {}
}

export const setStoredIsHighContrastMode = (isHighContrast: boolean) => {
  if (isHighContrast) {
    localStorage.setItem(HIGH_CONTRAST_KEY, '1')
  } else {
    localStorage.removeItem(HIGH_CONTRAST_KEY)
  }
}

export const getStoredIsHighContrastMode = () => {
  const highContrast = localStorage.getItem(HIGH_CONTRAST_KEY)
  return highContrast === '1'
}

export const getStoredDarkMode = () => {
  const prefersDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches
  return localStorage.getItem(THEME_KEY)
    ? localStorage.getItem(THEME_KEY) === THEME_DARK
    : prefersDarkMode
    ? true
    : false
}

export const setStoredDarkMode = (isDark: boolean) => {
  localStorage.setItem(THEME_KEY, isDark ? THEME_DARK : THEME_LIGHT)
}

export const getStoredHardMode = () => {
  return localStorage.getItem(HARD_MODE_KEY)
    ? localStorage.getItem(HARD_MODE_KEY) === HARD_MODE_HARD
    : false
}

export const setStoredHardMode = (isHard: boolean) => {
  localStorage.setItem(
    HARD_MODE_KEY,
    isHard ? HARD_MODE_HARD : HARD_MODE_NORMAL
  )
}

export const getStoredExpertMode = () => {
  return localStorage.getItem(EXPERT_MODE_KEY)
    ? localStorage.getItem(EXPERT_MODE_KEY) === EXPERT_MODE_EXPERT
    : false
}

export const setStoredExpertMode = (isExpert: boolean) => {
  localStorage.setItem(
    EXPERT_MODE_KEY,
    isExpert ? EXPERT_MODE_EXPERT : EXPERT_MODE_NORMAL
  )
}

export const getStoredRevealSpeed = () => {
  return localStorage.getItem(REVEAL_SPEED_KEY) ?? REVEAL_SPEED_FAST
}

export const setStoredRevealSpeed = (revealSpeed: string) => {
  localStorage.setItem(REVEAL_SPEED_KEY, revealSpeed)
}

export const getStoredCursorType = () => {
  return localStorage.getItem(CURSOR_TYPE_KEY) ?? CURSOR_TYPE_NORMAL
}

export const setStoredCursorType = (cursor: string) => {
  localStorage.setItem(CURSOR_TYPE_KEY, cursor)
}

export const getStoredHints = () => {
  return localStorage.getItem(ENABLE_HINTS_KEY) !== HINTS_DISABLE
}

export const setStoredHints = (enableHints: boolean) => {
  localStorage.setItem(
    ENABLE_HINTS_KEY,
    enableHints ? HINTS_ENABLE : HINTS_DISABLE
  )
}

export type Features = {
  pplus?: boolean
  ads?: boolean
}

const DEFAULT_FEATURES: Features = {
  pplus: true,
  ads: true,
}

export const getStoredFeatures = () => {
  const storedFeatures = localStorage.getItem(FEATURES_KEY)
  const features: Features = storedFeatures ? JSON.parse(storedFeatures) : {}
  const result: Features = { ...DEFAULT_FEATURES }
  for (const key in features) {
    ;(result as any)[key] = (features as any)[key]
  }
  return result
}

export const setStoredFeature = (name: keyof Features, setting: boolean) => {
  const features = getStoredFeatures()
  features[name] = setting
  localStorage.setItem(FEATURES_KEY, JSON.stringify(features))
}

export const getStoredIsSignedIn = () => {
  return localStorage.getItem(IS_SIGNED_IN_KEY) === '1'
}

export const setStoredIsSignedIn = (isSignedIn: boolean) => {
  localStorage.setItem(IS_SIGNED_IN_KEY, isSignedIn ? '1' : '0')
}

export const getStoredHasSeenHint = () => {
  return localStorage.getItem(HAS_SEEN_HINT_KEY) === '1'
}

export const setStoredHasSeenHint = (hasSeenHint: boolean) => {
  localStorage.setItem(HAS_SEEN_HINT_KEY, hasSeenHint ? '1' : '0')
}
